import { Injectable } from '@angular/core';

/** Applies the floating-label interaction to legacy native text inputs. */
@Injectable({ providedIn: 'root' })
export class FloatingLabelService {
  private observer?: MutationObserver;

  start(): void {
    this.decorateInputs();

    this.observer = new MutationObserver(() => this.decorateInputs());
    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['placeholder'],
      childList: true,
      subtree: true,
    });
  }

  private decorateInputs(): void {
    document.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
      if (!this.isTextInput(input)) {
        return;
      }

      if (input.dataset.floatingLabelReady === 'true') {
        this.syncLabelWithPlaceholder(input);
        return;
      }

      const host = input.parentElement;
      if (!host || input.hasAttribute('hlmInput') || input.closest('.p-float-label')) {
        return;
      }

      const labelText = this.getLabelText(input, host);
      if (!labelText) {
        return;
      }

      if (!input.id) {
        input.id = `floating-input-${crypto.randomUUID()}`;
      }

      let label = Array.from(host.children).find(
        (element): element is HTMLLabelElement =>
          element instanceof HTMLLabelElement && element.htmlFor === input.id
      );

      if (!label) {
        label = document.createElement('label');
        label.htmlFor = input.id;
        label.textContent = labelText;
        input.insertAdjacentElement('afterend', label);
      }

      label.classList.add('native-floating-label');
      input.placeholder = labelText;
      input.classList.add('native-floating-input');
      input.dataset.floatingLabelReady = 'true';
      host.classList.add('native-floating-field');
    });
  }

  private syncLabelWithPlaceholder(input: HTMLInputElement): void {
    const placeholder = input.placeholder.trim();
    const label = Array.from(input.parentElement?.children ?? []).find(
      (element): element is HTMLLabelElement =>
        element instanceof HTMLLabelElement && element.htmlFor === input.id
    );

    if (placeholder && label && label.textContent?.trim() !== placeholder) {
      label.textContent = placeholder;
    }
  }

  private isTextInput(input: HTMLInputElement): boolean {
    return ['text', 'email', 'tel', 'number', 'password'].includes(input.type);
  }

  private getLabelText(input: HTMLInputElement, host: HTMLElement): string {
    const directLabel = Array.from(host.children).find(
      (element): element is HTMLLabelElement =>
        element instanceof HTMLLabelElement && element.htmlFor === input.id
    );

    return (
      input.placeholder.trim() ||
      directLabel?.textContent?.trim() ||
      this.humanize(input.getAttribute('formControlName') || input.name || input.id)
    );
  }

  private humanize(value: string): string {
    return value
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (character) => character.toUpperCase());
  }
}
